import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaInfoCircle, FaCheck, FaTimes, FaComments, FaFilter, FaSort } from 'react-icons/fa';
import {
  getConnectionsByTypeAndStatusAsync,
  selectFilteredConnections,
  selectLoading,
  selectError,
  acceptConnectionAsync,
  rejectConnectionAsync
} from '../../features/connections/connectionSlice';
import { viewProfileAsync, selectUserProfile, selectProfileLoading, selectProfileError } from '../../features/wholesaler/wholesalerSlice';
import { toast } from 'react-toastify';
import RetailerNavbar from '../../features/retailer/components/RetailerNavbar';
import Pagination from '../../features/components/Pagination';
import Footer from '../../features/components/Footer';
import { productsPerPageWebsite } from '../../app/constants';

// Profile Modal Component - Same as WholesalerConnections
const ConnectionDialog = ({ connection, onClose, onAccept, onReject }) => {
  if (!connection) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Details</h3>
          <div className="space-y-3">
            <p><span className="font-medium">Username:</span> {connection.username}</p>
            <p><span className="font-medium">Firm Name:</span> {connection.firmname}</p>
            <p><span className="font-medium">Owner Name:</span> {connection.ownername}</p>
            <p><span className="font-medium">Connection Date:</span> {new Date(connection.Date).toLocaleDateString()}</p>
            <p><span className="font-medium">Status:</span> {connection.status}</p>
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Close
            </button>
            {connection.status === 'PENDING' && (
              <>
                <button
                  onClick={() => onReject(connection.username)}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Reject
                </button>
                <button
                  onClick={() => onAccept(connection.username)}
                  className="px-4 py-2 bg-yellow-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  Accept
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileModal = ({ isOpen, onClose, userProfile, profileLoading, profileError }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  User Profile
                </h3>
                <div className="mt-4">
                  {profileLoading ? (
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                    </div>
                  ) : profileError ? (
                    <div className="text-red-600">{profileError}</div>
                  ) : userProfile ? (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Shop Name</h3>
                        <p className="mt-1 text-sm text-gray-900">{userProfile.registeredFirmName}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Owner Name</h3>
                        <p className="mt-1 text-sm text-gray-900">{userProfile.ownerName}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">City</h3>
                        <p className="mt-1 text-sm text-gray-900">{userProfile.city}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Address</h3>
                        <p className="mt-1 text-sm text-gray-900">{userProfile.address}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Email</h3>
                        <p className="mt-1 text-sm text-gray-900">{userProfile.email}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                        <p className="mt-1 text-sm text-gray-900">{userProfile.phoneNumber}</p>
                      </div>
                      {userProfile.gstNumber && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">GST Number</h3>
                          <p className="mt-1 text-sm text-gray-900">{userProfile.gstNumber}</p>
                        </div>
                      )}
                      {userProfile.registrationNumber && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Registration Number</h3>
                          <p className="mt-1 text-sm text-gray-900">{userProfile.registrationNumber}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-500">No profile data available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-500 text-base font-medium text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RetailerConnections = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedUsername, setSelectedUsername] = useState(null);

  const connections = useSelector(selectFilteredConnections);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const userProfile = useSelector(selectUserProfile);
  const profileLoading = useSelector(selectProfileLoading);
  const profileError = useSelector(selectProfileError);

  const connectionType = location.pathname.includes('requests') ? 'received' : 'both';
  const connectionStatus = location.pathname.includes('requests') ? 'PENDING' : 'APPROVED';

  useEffect(() => {
    dispatch(getConnectionsByTypeAndStatusAsync({ type: connectionType, status: connectionStatus }));
  }, [dispatch, connectionType, connectionStatus]);

  const handleViewProfile = async (username) => {
    try {
      setSelectedUsername(username);
      setIsProfileOpen(true);
      await dispatch(viewProfileAsync(username)).unwrap();
    } catch (error) {
      toast.error('Failed to load profile');
      setIsProfileOpen(false);
    }
  };

  const handleAccept = async (username) => {
    try {
      await dispatch(acceptConnectionAsync(username)).unwrap();
      setSelectedConnection(null);
      toast.success('Connection accepted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to accept connection');
    }
  };

  const handleReject = async (username) => {
    try {
      await dispatch(rejectConnectionAsync(username)).unwrap();
      setSelectedConnection(null);
      toast.success('Connection rejected successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to reject connection');
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Sort connections
  const sortedConnections = [...connections].sort((a, b) => {
    const dateA = new Date(a.Date);
    const dateB = new Date(b.Date);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const totalPages = Math.ceil(sortedConnections.length / productsPerPageWebsite);
  const paginatedConnections = sortedConnections.slice(
    (currentPage - 1) * productsPerPageWebsite,
    currentPage * productsPerPageWebsite
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <RetailerNavbar />
      <div className="pt-20 max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {location.pathname.includes('requests') ? 'Connection Requests' : 'Approved Connections'}
            </h1>
            <button
              onClick={() => navigate(location.pathname.includes('requests') ? '/retailer/connections/approved' : '/retailer/connections/requests')}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              {location.pathname.includes('requests') ? 'View Approved' : 'View Requests'}
            </button>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 p-4 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && sortedConnections.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-500 text-lg">No {connectionType} connections found</p>
            </div>
          )}

          {!loading && !error && sortedConnections.length > 0 && (
            <div className="bg-white shadow overflow-x-auto sm:rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Firm Name
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner Name
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative">
                      <div className="flex items-center">
                        Connection Date
                        <button
                          onClick={toggleSortOrder}
                          className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                          <FaSort className={`h-4 w-4 transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedConnections.map((connection) => (
                    <tr key={connection.username}>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{connection.firmname}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{connection.ownername}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(connection.Date).toLocaleDateString()}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => handleViewProfile(connection.username)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Profile"
                          >
                            <FaInfoCircle className="h-5 w-5" />
                          </button>
                          {connectionStatus === 'PENDING' ? (
                            <>
                              <button
                                onClick={() => handleAccept(connection.username)}
                                className="text-green-600 hover:text-green-900"
                                title="Accept Connection"
                              >
                                <FaCheck className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleReject(connection.username)}
                                className="text-red-600 hover:text-red-900"
                                title="Reject Connection"
                              >
                                <FaTimes className="h-5 w-5" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {/* Handle chat */}}
                              className="text-gray-600 hover:text-gray-900"
                              title="Chat"
                            >
                              <FaComments className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && sortedConnections.length > 0 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={productsPerPageWebsite}
              />
            </div>
          )}
        </div>
      </div>

      {selectedConnection && (
        <ConnectionDialog
          connection={selectedConnection}
          onClose={() => setSelectedConnection(null)}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => {
          setIsProfileOpen(false);
          setSelectedUsername(null);
        }}
        userProfile={userProfile}
        profileLoading={profileLoading}
        profileError={profileError}
      />

      <Footer />
    </div>
  );
};

export default RetailerConnections;